import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TextField,
  FormControl,
  Box,
  Autocomplete,
  FormGroup,
  FormControlLabel,
  Switch,
  IconButton,
  CircularProgress,
} from '@mui/material';
import { debounce } from 'lodash';
import {
  AdminPublicTemplate,
  AdminTemplate,
  AdminUserTemplate,
  UserTemplateItem,
} from '../../../types/types';
import APIService from '../../../services/APIService';
import { Visibility } from '@mui/icons-material';

interface TemplatePickerProps {
  onTemplateSelect: (template_id: string) => void;
  userId?: string; // Optional userId prop
}

const TemplatePicker: React.FC<TemplatePickerProps> = ({
  onTemplateSelect,
  userId, // Accepting userId as a prop
}) => {
  const [availableTemplates, setAvailableTemplates] = useState<
    AdminPublicTemplate[] | AdminUserTemplate[]
  >([]);
  const [availableUserTemplates, setAvailableUserTemplates] = useState<UserTemplateItem[]>([]);
  const [userTemplatesMode, setUserTemplatesMode] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>();
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const hasSearched = useRef(false);

  // Only fetch user templates on mode change since they don't support search
  useEffect(() => {
    if (userTemplatesMode && userId) {
      fetchUserTemplates();
    }
  }, [userTemplatesMode, userId]);

  const fetchTemplates = useCallback(async (search: string = '') => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken) return;

    if (!userTemplatesMode) {
      setLoading(true);
      try {
        const response = await APIService.makeAPIGetRequest({
          requestString: `/template/getAllPublicTemplates?search=${search}`,
          accessToken: accessToken,
        });

        if (response.ok) {
          setAvailableTemplates(response.value.templates);
          hasSearched.current = true;
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        setAvailableTemplates([]);
      } finally {
        setLoading(false);
      }
    }
  }, [userTemplatesMode]);

  const fetchUserTemplates = async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (!accessToken || !userId) return;

    setLoading(true);
    try {
      const response = await APIService.makeAPIGetRequest({
        requestString: `/user/getTemplatesForUser?target_user_id=${userId}`,
        accessToken: accessToken,
      });

      if (response.ok) {
        setAvailableUserTemplates(response.value.templates);
      }
    } catch (error) {
      console.error('Failed to fetch user templates:', error);
      setAvailableUserTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  // Create debounced search function
  const debouncedFetchTemplates = useRef(
    debounce((search: string) => {
      fetchTemplates(search);
    }, 300)
  ).current;

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      debouncedFetchTemplates.cancel();
    };
  }, [debouncedFetchTemplates]);

  const handleInputChange = (_event: React.ChangeEvent<{}>, value: string, reason: string) => {
    setInputValue(value);

    // Only search public templates (user templates don't support search)
    if (!userTemplatesMode && reason === 'input') {
      debouncedFetchTemplates.cancel();

      if (value.trim().length >= 2) {
        debouncedFetchTemplates(value.trim());
      } else if (value.trim().length === 0) {
        setAvailableTemplates([]);
        hasSearched.current = false;
      }
    }
  };

  const handleTemplateChange = (
    event: any,
    value: AdminPublicTemplate | AdminUserTemplate | null,
  ) => {
    if (value) {
      setSelectedTemplateId(value.template_id);
      onTemplateSelect(value.template_id);
    } else {
      setSelectedTemplateId('');
      onTemplateSelect('');
    }
  };

  const handleUserTemplateChange = (event: any, value: UserTemplateItem | null) => {
    if (value) {
      onTemplateSelect(value.template_id);
    }
  };

  const handleModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUserTemplatesMode(event.target.checked);
    setInputValue(''); // Clear input when switching modes
    setAvailableTemplates([]);
    setAvailableUserTemplates([]);
    hasSearched.current = false;
  };

  const handleOpen = () => {
    setOpen(true);
    // Fetch initial set if in user mode or if nothing searched yet
    if (userTemplatesMode && userId && availableUserTemplates.length === 0) {
      fetchUserTemplates();
    } else if (!userTemplatesMode && !hasSearched.current && inputValue.length < 2) {
      fetchTemplates('');
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <FormControl fullWidth>
        {userTemplatesMode ? (
          <Autocomplete
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            options={availableUserTemplates}
            loading={loading}
            getOptionLabel={option => (option.display_name ? option.display_name : '')}
            renderInput={params => (
              <TextField
                {...params}
                label="Choose a user template"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={handleUserTemplateChange}
            noOptionsText={loading ? 'Loading...' : 'No templates found'}
          />
        ) : (
          <Autocomplete
            open={open}
            onOpen={handleOpen}
            onClose={handleClose}
            options={availableTemplates}
            loading={loading}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            getOptionLabel={option =>
              option.public_name ? option.public_name : option.template_name
            }
            renderInput={params => (
              <TextField
                {...params}
                label="Choose a template"
                variant="outlined"
                placeholder="Type at least 2 characters to search..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            onChange={handleTemplateChange}
            noOptionsText={
              inputValue.trim().length < 2
                ? 'Type at least 2 characters to search'
                : loading
                ? 'Searching...'
                : 'No templates found'
            }
          />
        )}

        {userId && (
          <FormGroup>
            <FormControlLabel
              control={<Switch checked={userTemplatesMode} onChange={handleModeChange} />}
              label="User Templates Mode"
            />
          </FormGroup>
        )}
      </FormControl>
      {selectedTemplateId && (
        <IconButton
          aria-label="preview"
          onClick={() => {
            window.open(
              `${window.location.origin}/admin/publictemplate/${selectedTemplateId}`,
              '_blank',
            );
          }}
        >
          <Visibility />
        </IconButton>
      )}
    </Box>
  );
};

export default TemplatePicker;
